/**
 * Route legacy (old-billing) users through the new AI trial upgrade flow.
 *
 * Legacy + active users (old billing system, live subscription) should be sent to the new
 * `/customers/legacy-ai-upgrade/` page instead of the classic `/customers/change_plan/` flow
 * (or a feature-locked `/domains/inactive-subscription` link) whenever they hit an upgrade /
 * reactivate CTA. Everyone else keeps the original destination.
 *
 * `user_type` ("traditional" === legacy/old system) and `is_active` come from the
 * `/api/v1/entitlements/` response, cached on `window.user_permissions` by base-sidebar.html.
 */

export const AI_TRIAL_UPGRADE_URL = '/customers/legacy-ai-upgrade/';

/**
 * True when the current user is on the old billing system and has a live subscription.
 */
export const isLegacyActiveUser = () => {
    const permissions = window.user_permissions || {};
    return permissions.user_type === 'traditional' && Boolean(permissions.is_active);
};

/**
 * Return the AI trial upgrade URL for legacy + active users, otherwise the original destination.
 *
 * @param {string} fallbackUrl - Destination used for every non legacy-active user.
 * @returns {string}
 */
export const getUpgradeUrl = (fallbackUrl) =>
    isLegacyActiveUser() ? AI_TRIAL_UPGRADE_URL : fallbackUrl;

/**
 * True once the `/api/v1/entitlements/` payload has actually landed on `window.user_permissions`.
 *
 * `user_type` is always present in that response, so its absence means the permissions object is
 * still the empty placeholder React starts with — nothing permission-driven should render yet.
 */
const arePermissionsLoaded = (permissions) => Boolean(permissions && permissions.user_type);

/**
 * True when the "free AI Tracking for one month" offer should be promoted to the current user.
 *
 * The offer targets every user who is not already entitled to AI Tracking — not only legacy
 * (old-billing) users — so `ai_tracking` from the entitlements payload is the only gate. The
 * loaded check keeps the banner from flashing for users who do have AI Tracking while the
 * entitlements request is still in flight.
 *
 * @param {object} [permissions] - Entitlements payload; defaults to `window.user_permissions`.
 * @returns {boolean}
 */
export const shouldPromoteFreeAiTracking = (permissions = window.user_permissions || {}) =>
    arePermissionsLoaded(permissions) && !permissions.ai_tracking;
